/**
 * Payments contracts (03-modules.md §7, 04-data-model.md "payments", 05-api.md
 * "Payments"; M3 task 4): tenders (cash with change, card and UPI recorded, other),
 * split and partial payment, tips, payment-mode corrections, online payment intents
 * (Razorpay: UPI intent / QR / checkout) confirmed by the gateway's webhook or the
 * client's signed result, settlement with the GST invoice number, and the
 * pending-settlement queue.
 *
 * An order's payment record is the sum of its tenders and nothing else. Each tender
 * has an amount applied to the bill (`amountPaise`), an optional tip on top
 * (`tipPaise`, not part of the bill, not taxed) and, for cash, the change handed
 * back: `tenderedPaise = amountPaise + tipPaise + changePaise`. Overpayment exists
 * only as cash change or a tip (03-modules.md §7 rules).
 *
 * Money is integer paise (CLAUDE.md rule 2). Card data never touches our systems: a
 * card tender records a reference (approval code, last digits) at most.
 */
import { z } from 'zod';
import { invoiceBuyerInputSchema, invoiceSchema } from './accounting.js';
import { businessDateSchema, isoTimestampSchema, pageQuerySchema, paginated } from './common.js';
import { defineEvent } from './events.js';
import { uuidSchema } from './ids.js';
import { nonNegativePaiseSchema, paiseSchema } from './money.js';
import { ORDER_STATUSES, orderSchema, reasonCodeSchema } from './orders.js';
import { PAYMENT_METHOD_KINDS } from './org.js';

// ---------------------------------------------------------------------------
// Vocabulary

/**
 * `payment.status`: `recorded` (a tender taken at the till), `confirmed` (an online
 * payment the gateway confirmed), `voided` (reversed before settlement; never deleted).
 */
export const PAYMENT_STATUSES = ['recorded', 'confirmed', 'voided'] as const;
export type PaymentStatusName = (typeof PAYMENT_STATUSES)[number];

/**
 * Tenders the till records by hand. `razorpay` (online) payments come only from a
 * gateway confirmation; khata and reward redemptions come through the customers
 * module (M5).
 */
export const MANUAL_TENDER_KINDS = ['cash', 'card', 'upi', 'other'] as const;
export type ManualTenderKind = (typeof MANUAL_TENDER_KINDS)[number];

/** The tender kind of every payment a gateway confirmed (whatever method the payer used). */
export const ONLINE_TENDER_KIND = 'razorpay' as const;

/** Methods an online payment intent may offer the payer. */
export const INTENT_METHODS = ['upi', 'card', 'netbanking'] as const;

/**
 * `payment_intent.status`: `created` (waiting for the payer; a failed attempt keeps it
 * open, the payer may retry; past `expiresAt` it is no longer offered, but a payment
 * that still arrives for it is applied when the order can take it), `paid` (a
 * captured payment was applied to the order), `rejected` (a captured payment could
 * not be applied: see `rejectionReason`; the money is due back to the payer,
 * `refundDuePaise`, refunds are handled outside v3.0), `cancelled` (the till withdrew
 * it, or the order was settled: the gateway's QR is closed; a payment that still
 * arrives is refused as `intent_cancelled` and due back).
 */
export const INTENT_STATUSES = ['created', 'paid', 'rejected', 'cancelled'] as const;

/** Why a payment request was withdrawn. */
export const INTENT_CANCEL_REASONS = ['staff', 'order_settled'] as const;

/**
 * Why a gateway's confirmation was not applied (alerted as
 * `payment.confirmation_rejected`):
 * - `amount_mismatch` / `currency_mismatch` / `order_mismatch`: the confirmation does
 *   not match the intent it names (a tampered or misrouted payment);
 * - `unknown_intent`: no intent of this provider has that gateway order id;
 * - `already_paid`: the intent was paid by another payment;
 * - `order_not_payable`: the order was settled, cancelled or rejected meanwhile (a
 *   late payment after a manual settlement), or owes less than the payment now;
 * - `intent_cancelled`: the request was withdrawn (by the till, or when the order was
 *   settled) before the payment arrived.
 */
export const CONFIRMATION_REJECTIONS = [
  'amount_mismatch',
  'currency_mismatch',
  'order_mismatch',
  'unknown_intent',
  'already_paid',
  'order_not_payable',
  'intent_cancelled',
] as const;
export type ConfirmationRejection = (typeof CONFIRMATION_REJECTIONS)[number];

/** Currency of every bill (the outlet's). */
export const PAYMENT_CURRENCY = 'INR';

/** Luhn check of a digit string (card numbers pass it). */
function luhnValid(digits: string): boolean {
  let sum = 0;
  for (let i = 0; i < digits.length; i += 1) {
    let d = Number(digits[digits.length - 1 - i]);
    if (i % 2 === 1) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
  }
  return sum % 10 === 0;
}

/**
 * Whether the text holds what looks like a full card number (PAN): a run of 13 to 19
 * digits, optionally grouped by spaces or dashes, that passes the Luhn check.
 */
export function containsCardNumber(text: string): boolean {
  for (const run of text.match(/\d(?:[ -]?\d)+/g) ?? []) {
    const digits = run.replace(/[ -]/g, '');
    if (digits.length >= 13 && digits.length <= 19 && luhnValid(digits)) return true;
  }
  return false;
}

/**
 * A tender's reference: a card's approval code or last digits, a UPI reference (UTR).
 * Card data never touches our systems (03-modules.md §7): text holding a full card
 * number is refused.
 */
export const tenderReferenceSchema = z
  .string()
  .trim()
  .min(1)
  .max(100)
  .refine((v) => !containsCardNumber(v), {
    message: 'Never a card number: record the approval code or the last 4 digits',
  });
const referenceSchema = tenderReferenceSchema;
const noteSchema = z.string().trim().max(500);

// ---------------------------------------------------------------------------
// Views

export const paymentCorrectionSchema = z.object({
  id: uuidSchema,
  fromKind: z.enum(PAYMENT_METHOD_KINDS),
  toKind: z.enum(PAYMENT_METHOD_KINDS),
  fromMethodId: uuidSchema.nullable(),
  toMethodId: uuidSchema.nullable(),
  fromAmountPaise: paiseSchema,
  toAmountPaise: paiseSchema,
  fromReference: z.string().nullable(),
  toReference: z.string().nullable(),
  reasonCode: z.string(),
  note: z.string().nullable(),
  actorId: uuidSchema.nullable(),
  approvedBy: uuidSchema.nullable(),
  at: isoTimestampSchema,
});

/** One tender (04-data-model.md `payment`), with its tip and corrections. */
export const paymentSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  orderId: uuidSchema,
  /** The order's business date (the sale's day). */
  businessDate: businessDateSchema,
  methodKind: z.enum(PAYMENT_METHOD_KINDS),
  /** The outlet's payment method (org), when one was named or found. */
  methodId: uuidSchema.nullable(),
  /** Applied to the bill. */
  amountPaise: paiseSchema,
  /** On top of the bill, not taxed. */
  tipPaise: paiseSchema,
  tipStaffId: uuidSchema.nullable(),
  /** What the guest handed over or was charged: amount + tip + change. */
  tenderedPaise: paiseSchema,
  /** Cash only. */
  changePaise: paiseSchema,
  reference: z.string().nullable(),
  /** The connector (`razorpay`) for online payments. */
  provider: z.string().nullable(),
  providerPaymentId: z.string().nullable(),
  /** What the payer used at the gateway (upi, card, netbanking, other). */
  providerMethod: z.string().nullable(),
  intentId: uuidSchema.nullable(),
  status: z.enum(PAYMENT_STATUSES),
  recordedBy: uuidSchema.nullable(),
  terminalId: uuidSchema.nullable(),
  createdAt: isoTimestampSchema,
  voidedAt: isoTimestampSchema.nullable(),
  voidedBy: uuidSchema.nullable(),
  voidReasonCode: z.string().nullable(),
  voidNote: z.string().nullable(),
  corrections: z.array(paymentCorrectionSchema),
  version: z.int(),
});
export type PaymentView = z.output<typeof paymentSchema>;

export const paymentParamsSchema = z.object({ outletId: uuidSchema, paymentId: uuidSchema });

export const paymentListQuerySchema = pageQuerySchema.extend({
  businessDate: businessDateSchema.optional(),
  orderId: uuidSchema.optional(),
  status: z.enum(PAYMENT_STATUSES).optional(),
});
export const paymentListSchema = paginated(paymentSchema);

/** An online payment request (04-data-model.md `payment_intent`). */
export const paymentIntentSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  orderId: uuidSchema,
  /** The connector that created it (`razorpay`). */
  provider: z.string(),
  installId: uuidSchema,
  /** The gateway's order id, bound to our order id and amount. */
  providerOrderId: z.string(),
  amountPaise: paiseSchema,
  currency: z.string(),
  status: z.enum(INTENT_STATUSES),
  /** Created, unpaid and past `expiresAt`: create a new one. */
  expired: z.boolean(),
  expiresAt: isoTimestampSchema,
  /** `upi://pay?…`: opens the payer's UPI app; also the content of the QR to show. */
  upiIntentUrl: z.string().nullable(),
  /** The QR's content to render, when the gateway gives one. */
  qrPayload: z.string().nullable(),
  /**
   * An image of the gateway's single-use QR for this request (the till shows it to
   * the diner), when the gateway made one; it closes when the request expires or is
   * cancelled.
   */
  qrImageUrl: z.string().nullable(),
  /** What the gateway's client checkout needs (public key id, gateway order id); never secrets. */
  checkout: z.record(z.string(), z.unknown()).nullable(),
  paymentId: uuidSchema.nullable(),
  /** The payer's last failed attempt, in plain words. */
  failureReason: z.string().nullable(),
  rejectionReason: z.enum(CONFIRMATION_REJECTIONS).nullable(),
  cancelledAt: isoTimestampSchema.nullable(),
  cancelReason: z.enum(INTENT_CANCEL_REASONS).nullable(),
  /** Captured money that could not be applied (refund outside v3.0). */
  refundDuePaise: paiseSchema,
  createdAt: isoTimestampSchema,
  updatedAt: isoTimestampSchema,
  version: z.int(),
});
export type PaymentIntentView = z.output<typeof paymentIntentSchema>;

export const paymentIntentParamsSchema = z.object({ outletId: uuidSchema, intentId: uuidSchema });

/** One order waiting for payment that the till parked (`markPendingSettlement`). */
export const pendingSettlementSchema = z.object({
  orderId: uuidSchema,
  orderNo: z.int(),
  businessDate: businessDateSchema,
  status: z.enum(ORDER_STATUSES),
  totalPaise: paiseSchema,
  paidPaise: paiseSchema,
  duePaise: paiseSchema,
  reasonCode: z.string(),
  note: z.string().nullable(),
  markedBy: uuidSchema.nullable(),
  markedAt: isoTimestampSchema,
});
export const pendingSettlementListSchema = z.object({ data: z.array(pendingSettlementSchema) });

// ---------------------------------------------------------------------------
// Commands

/** `POST {o}/orders/{id}/payments`: record a tender. */
export const recordTenderSchema = z.object({
  /** The payment id when the till made it (offline); the server makes one otherwise. */
  id: uuidSchema.optional(),
  methodKind: z.enum(MANUAL_TENDER_KINDS),
  /** The outlet's payment method of that kind; the first enabled one by default. */
  methodId: uuidSchema.optional(),
  /** Applied to the bill: at most what is due. */
  amountPaise: nonNegativePaiseSchema,
  /** Cash handed over (change = tendered − amount − tip); other kinds: amount + tip. */
  tenderedPaise: nonNegativePaiseSchema.optional(),
  tipPaise: nonNegativePaiseSchema.optional(),
  /** Who the tip is for (a staff member); the outlet's tip pool when absent. */
  tipStaffId: uuidSchema.optional(),
  /** Card approval code or last digits, UPI reference (UTR); never a card number. */
  reference: referenceSchema.optional(),
});

/** A tender command's answer: the payment and the order after it. */
export const tenderResultSchema = z.object({ payment: paymentSchema, order: orderSchema });

/** `POST {o}/payments/{id}/void`. */
export const voidPaymentSchema = z.object({
  reasonCode: reasonCodeSchema,
  note: noteSchema.optional(),
});

/**
 * `POST {o}/payments/{id}/correct`: the tender's mode (kind, method, reference) after
 * billing, settled or not; its amount (and cash handed over) only before settlement.
 */
export const correctPaymentSchema = z
  .object({
    methodKind: z.enum(MANUAL_TENDER_KINDS).optional(),
    methodId: uuidSchema.optional(),
    reference: referenceSchema.nullable().optional(),
    amountPaise: nonNegativePaiseSchema.optional(),
    tenderedPaise: nonNegativePaiseSchema.optional(),
    reasonCode: reasonCodeSchema,
    note: noteSchema.optional(),
  })
  .refine(
    (v) =>
      v.methodKind !== undefined ||
      v.methodId !== undefined ||
      v.reference !== undefined ||
      v.amountPaise !== undefined ||
      v.tenderedPaise !== undefined,
    { message: 'Name what to correct', path: ['methodKind'] },
  );

/** `POST {o}/orders/{id}/payment-intents`. */
export const createPaymentIntentSchema = z.object({
  /** What to collect; what is due by default. */
  amountPaise: nonNegativePaiseSchema.optional(),
  methods: z.array(z.enum(INTENT_METHODS)).min(1).max(3).optional(),
  /** The payer's phone, when the gateway wants one (never stored by payments). */
  customerPhone: z.string().trim().min(4).max(20).optional(),
});

/** `POST {o}/payment-intents/{id}/cancel`: withdraw an unpaid request (e.g. paid in cash instead). */
export const cancelPaymentIntentSchema = z.object({ note: noteSchema.optional() });

/** `POST {o}/payment-intents/{id}/verify`: what the gateway's client checkout returned. */
export const verifyPaymentSchema = z.object({
  /** Passed to the connector as is (its own field names; core never reads them). */
  result: z.record(z.string().max(100), z.string().max(1000)),
});

/** `POST {o}/orders/{id}/settle`. */
export const settleOrderSchema = z.object({
  /** A B2B invoice's buyer; B2C without. */
  buyer: invoiceBuyerInputSchema.optional(),
  /** The invoice id to issue under (offline clients make it); a new one otherwise. */
  invoiceId: uuidSchema.optional(),
});

export const settleResultSchema = z.object({ order: orderSchema, invoice: invoiceSchema });

/** What confirming an online payment did. */
export const confirmResultSchema = z.object({
  intent: paymentIntentSchema,
  payment: paymentSchema.nullable(),
  order: orderSchema,
  /** Issued when the payment completed a billed order (it settled at once). */
  invoice: invoiceSchema.nullable(),
});

/** `POST {o}/orders/{id}/pending-settlement`. */
export const markPendingSettlementSchema = z.object({
  reasonCode: reasonCodeSchema,
  note: noteSchema.optional(),
});

// ---------------------------------------------------------------------------
// Events (03-modules.md §7, plus the ones below the list)

/**
 * What every tender event carries: the tender and the order after it, so the board
 * and the table map follow paid amounts and statuses without a refetch. No personal
 * data and no references (projected to the outlet-wide pos room).
 */
const paymentRef = {
  paymentId: uuidSchema,
  orderId: uuidSchema,
  orderNo: z.int(),
  businessDate: businessDateSchema,
  methodKind: z.enum(PAYMENT_METHOD_KINDS),
  methodId: uuidSchema.nullable(),
  amountPaise: paiseSchema,
  tipPaise: paiseSchema,
  changePaise: paiseSchema,
  status: z.enum(PAYMENT_STATUSES),
  provider: z.string().nullable(),
  orderStatus: z.enum(ORDER_STATUSES),
  orderTotalPaise: paiseSchema,
  orderPaidPaise: paiseSchema,
  orderVersion: z.int(),
};

export const paymentRecordedV1 = defineEvent(
  'payment.recorded',
  1,
  z.object({ ...paymentRef, tipStaffId: uuidSchema.nullable() }),
);

/** An online payment the gateway confirmed was applied to its order. */
export const paymentConfirmedV1 = defineEvent(
  'payment.confirmed',
  1,
  z.object({
    ...paymentRef,
    intentId: uuidSchema,
    providerPaymentId: z.string(),
    source: z.enum(['webhook', 'client']),
  }),
);

/** The payer's attempt failed at the gateway (the intent stays open for a retry). */
export const paymentFailedV1 = defineEvent(
  'payment.failed',
  1,
  z.object({
    intentId: uuidSchema,
    orderId: uuidSchema,
    provider: z.string(),
    providerPaymentId: z.string().nullable(),
    amountPaise: paiseSchema,
    reason: z.string(),
  }),
);

/** A tender reversed before settlement (not in 03-modules.md §7's list; rule 4). */
export const paymentVoidedV1 = defineEvent(
  'payment.voided',
  1,
  z.object({ ...paymentRef, reasonCode: z.string() }),
);

export const paymentCorrectedV1 = defineEvent(
  'payment.corrected',
  1,
  z.object({
    ...paymentRef,
    correctionId: uuidSchema,
    fromKind: z.enum(PAYMENT_METHOD_KINDS),
    toKind: z.enum(PAYMENT_METHOD_KINDS),
    fromAmountPaise: paiseSchema,
    toAmountPaise: paiseSchema,
    reasonCode: z.string(),
  }),
);

/** An online payment request was created at the gateway (not in §7's list; rule 4). */
export const paymentIntentCreatedV1 = defineEvent(
  'payment.intent_created',
  1,
  z.object({
    intentId: uuidSchema,
    orderId: uuidSchema,
    provider: z.string(),
    amountPaise: paiseSchema,
    expiresAt: isoTimestampSchema,
  }),
);

/**
 * A payment request was withdrawn (not in §7's list; rule 4): by the till, or because
 * its order was settled. The gateway's QR is closed after it (outside the transaction).
 */
export const paymentIntentCancelledV1 = defineEvent(
  'payment.intent_cancelled',
  1,
  z.object({
    intentId: uuidSchema,
    orderId: uuidSchema,
    provider: z.string(),
    amountPaise: paiseSchema,
    reason: z.enum(INTENT_CANCEL_REASONS),
  }),
);

/**
 * A captured payment could not be applied (03-modules.md §7: "rejected and
 * alerted"): mismatched amount, currency or order, an unknown intent, or a payment
 * for an order no longer payable. The money is due back to the payer.
 */
export const paymentConfirmationRejectedV1 = defineEvent(
  'payment.confirmation_rejected',
  1,
  z.object({
    intentId: uuidSchema.nullable(),
    orderId: uuidSchema.nullable(),
    provider: z.string(),
    providerOrderId: z.string(),
    providerPaymentId: z.string().nullable(),
    amountPaise: paiseSchema,
    currency: z.string(),
    reason: z.enum(CONFIRMATION_REJECTIONS),
    refundDuePaise: paiseSchema,
  }),
);

/** The till parked a billed order as pending settlement (`markPendingSettlement`). */
export const settlementPendingMarkedV1 = defineEvent(
  'settlement.pending_marked',
  1,
  z.object({
    orderId: uuidSchema,
    orderNo: z.int(),
    businessDate: businessDateSchema,
    reasonCode: z.string(),
    duePaise: paiseSchema,
  }),
);

export const PAYMENT_EVENTS = [
  paymentRecordedV1,
  paymentConfirmedV1,
  paymentFailedV1,
  paymentVoidedV1,
  paymentCorrectedV1,
  paymentIntentCreatedV1,
  paymentIntentCancelledV1,
  paymentConfirmationRejectedV1,
  settlementPendingMarkedV1,
] as const;
